// Curated category map. Keys live in the URL; tokens are matched (case-insensitive)
// against the qfap_tags text field, whose values look like "Concert" or
// "Art contemporain;Atelier;Expo".
export const CATEGORIES = [
  { key: "music", label: "Musique / Concert", tokens: ["Concert", "Spectacle musical"] },
  { key: "expo", label: "Exposition", tokens: ["Expo"] },
  { key: "theater", label: "Théâtre", tokens: ["Théâtre"] },
  { key: "dance", label: "Danse", tokens: ["Danse"] },
  { key: "cinema", label: "Écrans / Cinéma", tokens: ["Ecrans"] },
  { key: "workshop", label: "Atelier", tokens: ["Atelier"] },
  { key: "conference", label: "Conférence", tokens: ["Conférence"] },
  { key: "family", label: "Enfants / Famille", tokens: ["Enfants"] },
  { key: "festival", label: "Festival", tokens: ["Festival"] },
  { key: "walk", label: "Balade urbaine", tokens: ["Balade urbaine"] },
  { key: "art", label: "Art contemporain", tokens: ["Art contemporain"] },
  { key: "nature", label: "Nature", tokens: ["Nature"] },
];

export const WHEN_OPTIONS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "tomorrow", label: "Demain" },
  { key: "weekend", label: "Ce week-end" },
  { key: "week", label: "Cette semaine" },
];

export const A11Y_OPTIONS = [
  { key: "pmr", label: "Mobilité réduite" },
  { key: "blind", label: "Déficience visuelle" },
  { key: "deaf", label: "Déficience auditive" },
];

export const SORT_OPTIONS = [
  { key: "date", label: "Date la plus proche", orderBy: "date_start asc,title asc" },
  { key: "recent", label: "Ajouté récemment", orderBy: "updated_at desc" },
  { key: "title", label: "Titre A → Z", orderBy: "title asc" },
];

const ALL_CAT_KEYS = new Set(CATEGORIES.map((c) => c.key));
const ALL_A11Y_KEYS = new Set(A11Y_OPTIONS.map((a) => a.key));
const ALL_WHEN_KEYS = new Set(WHEN_OPTIONS.map((w) => w.key));

function parseCSV(value, allowed) {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => allowed.has(s));
}

function isValidISODate(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

export function parseFilters(searchParams) {
  const q = (searchParams.get("q") || "").trim();
  const whenRaw = searchParams.get("when");
  const when = ALL_WHEN_KEYS.has(whenRaw) ? whenRaw : null;
  const date = isValidISODate(searchParams.get("date")) ? searchParams.get("date") : null;
  const cats = parseCSV(searchParams.get("cats"), ALL_CAT_KEYS);
  const priceRaw = searchParams.get("price");
  const price = ["free", "paid"].includes(priceRaw) ? priceRaw : null;
  const access = parseCSV(searchParams.get("access"), ALL_A11Y_KEYS);
  return { q, when, date, cats, price, access };
}

export function hasAnyFilter(f) {
  return Boolean(
    f.q || f.when || f.date || f.cats.length || f.price || f.access.length
  );
}

export function countActiveFilters(f) {
  let n = 0;
  if (f.when || f.date) n += 1;
  if (f.cats.length) n += f.cats.length;
  if (f.price) n += 1;
  if (f.access.length) n += f.access.length;
  return n;
}

function fmtISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function computeDateRange({ when, date }) {
  if (date) return { from: date, to: date };
  if (!when) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (when) {
    case "today":
      return { from: fmtISO(today), to: fmtISO(today) };
    case "tomorrow": {
      const t = new Date(today);
      t.setDate(t.getDate() + 1);
      return { from: fmtISO(t), to: fmtISO(t) };
    }
    case "weekend": {
      const day = today.getDay(); // 0=Sun, 6=Sat
      const sat = new Date(today);
      const sun = new Date(today);
      if (day === 0) {
        sat.setDate(sat.getDate() - 1);
      } else if (day === 6) {
        sun.setDate(sun.getDate() + 1);
      } else {
        sat.setDate(today.getDate() + (6 - day));
        sun.setDate(sat.getDate() + 1);
      }
      return { from: fmtISO(sat), to: fmtISO(sun) };
    }
    case "week": {
      const end = new Date(today);
      const day = today.getDay();
      const daysToSunday = day === 0 ? 0 : 7 - day;
      end.setDate(today.getDate() + daysToSunday);
      return { from: fmtISO(today), to: fmtISO(end) };
    }
    default:
      return null;
  }
}

function escapeQuotes(s) {
  return s.replace(/"/g, '\\"');
}

// Fields the free-text query is matched against. Scoping to these (instead of
// the API's global `search`, which also matches address/transport/contact text)
// keeps results on-topic.
const TEXT_FIELDS = ["title", "lead_text", "qfap_tags"];

// Build a where clause from the free-text query: every word must appear in at
// least one meaningful field. Word-level LIKE matching is accent/case
// insensitive on the dataset.
export function buildTextClause(q) {
  const words = (q || "")
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);
  if (!words.length) return "";

  const perWord = words.map((word) => {
    const safe = escapeQuotes(word);
    const ors = TEXT_FIELDS.map((f) => `${f} LIKE "${safe}"`);
    return `(${ors.join(" OR ")})`;
  });
  return perWord.join(" AND ");
}

// Keeps finished events out of the default listing. Not applied when the user
// picked a period themselves — they may well be looking at a past date.
export function notFinishedClause() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return `date_end >= "${fmtISO(today)}T00:00:00"`;
}

export function buildWhereClause(filters) {
  const clauses = [];

  const textClause = buildTextClause(filters.q);
  if (textClause) clauses.push(textClause);

  const range = computeDateRange(filters);
  if (range) {
    clauses.push(
      `date_start <= "${range.to}T23:59:59" AND date_end >= "${range.from}T00:00:00"`
    );
  }

  if (filters.cats.length) {
    const tokens = [];
    for (const key of filters.cats) {
      const cat = CATEGORIES.find((c) => c.key === key);
      if (cat) cat.tokens.forEach((t) => tokens.push(t));
    }
    if (tokens.length) {
      const parts = tokens.map((t) => `qfap_tags LIKE "${escapeQuotes(t)}"`);
      clauses.push(`(${parts.join(" OR ")})`);
    }
  }

  if (filters.price === "free") clauses.push(`price_type = "gratuit"`);
  else if (filters.price === "paid") clauses.push(`price_type = "payant"`);

  for (const key of filters.access) {
    clauses.push(`${key} = 1`);
  }

  return clauses.length ? clauses.join(" AND ") : "";
}

const DEFAULT_SORT = SORT_OPTIONS[0];

export function parseSort(searchParams) {
  const raw = searchParams.get("sort");
  return SORT_OPTIONS.some((s) => s.key === raw) ? raw : DEFAULT_SORT.key;
}

export function orderByFor(sortKey) {
  const found = SORT_OPTIONS.find((s) => s.key === sortKey);
  return (found || DEFAULT_SORT).orderBy;
}

const dateLabelFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function withoutValue(list, value) {
  const next = list.filter((v) => v !== value);
  return next.length ? next.join(",") : null;
}

// Flattens the active filters into removable chips for the results header.
// Each entry carries the URL patch that clears just that filter.
export function describeActiveFilters(f) {
  const out = [];

  if (f.q) {
    out.push({ id: "q", label: `« ${f.q} »`, patch: { q: null } });
  }

  if (f.when) {
    const opt = WHEN_OPTIONS.find((w) => w.key === f.when);
    if (opt) out.push({ id: "when", label: opt.label, patch: { when: null } });
  } else if (f.date) {
    const parsed = new Date(`${f.date}T12:00:00`);
    const label = Number.isNaN(parsed.getTime())
      ? f.date
      : dateLabelFmt.format(parsed);
    out.push({ id: "date", label, patch: { date: null } });
  }

  for (const key of f.cats) {
    const cat = CATEGORIES.find((c) => c.key === key);
    if (cat) {
      out.push({
        id: `cat-${key}`,
        label: cat.label,
        patch: { cats: withoutValue(f.cats, key) },
      });
    }
  }

  if (f.price) {
    out.push({
      id: "price",
      label: f.price === "free" ? "Gratuit" : "Payant",
      patch: { price: null },
    });
  }

  for (const key of f.access) {
    const a11y = A11Y_OPTIONS.find((a) => a.key === key);
    if (a11y) {
      out.push({
        id: `access-${key}`,
        label: a11y.label,
        patch: { access: withoutValue(f.access, key) },
      });
    }
  }

  return out;
}
